if (!customElements.get('sofa-extras')) {
  customElements.define('sofa-extras', class SofaExtras extends HTMLElement {
    connectedCallback() {
      window.setTimeout(() => this.initialize(), 0);
    }

    initialize() {
      if (this.dataset.initialized === 'true') return;
      this.inputs = Array.from(this.querySelectorAll('.sofa-extras__input'));
      if (!this.inputs.length) return;
      this.dataset.initialized = 'true';
      this.toggle = this.querySelector('.sofa-extras__toggle');
      this.summary = this.querySelector('.sofa-extras__summary');
      this.total = this.querySelector('.sofa-extras__total');
      this.error = this.querySelector('.sofa-extras__error');
      this.form = document.getElementById(this.dataset.productFormId);
      this.variantPrices = this.getVariantPrices();
      this.variantTitles = this.getVariantTitles();
      this.handleSubmit = this.onSubmit.bind(this);

      if (this.inputs.length > 6 && this.toggle) {
        this.toggle.hidden = false;
        this.toggle.addEventListener('click', () => {
          const expanded = this.classList.toggle('is-expanded');
          this.toggle.setAttribute('aria-expanded', String(expanded));
        });
      }

      this.inputs.forEach((input) => input.addEventListener('change', () => this.onInputChange(input)));
      if (this.form) {
        this.form.addEventListener('submit', this.handleSubmit, true);
        const productInfo = document.getElementById(`ProductInfo-${this.dataset.sectionId}`);
        if (productInfo) {
          productInfo.addEventListener('change', () => {
            window.setTimeout(() => this.updateDisplayedPrice(), 60);
          });
        }
      }
      this.updateSummary();
    }

    getVariantPrices() {
      const data = this.querySelector('.sofa-extras__variant-prices');
      if (!data) return {};
      try {
        return JSON.parse(data.textContent);
      } catch (error) {
        return {};
      }
    }

    getVariantTitles() {
      const data = this.querySelector('.sofa-extras__variant-titles');
      if (!data) return {};
      try {
        return JSON.parse(data.textContent);
      } catch (error) {
        return {};
      }
    }

    getCurrentVariantTitle() {
      if (!this.form) return '';
      const variantInput = this.form.querySelector('[name="id"]');
      const title = String(this.variantTitles[variantInput ? variantInput.value : ''] || '');
      return title === 'Default Title' ? '' : title;
    }

    getCurrentVariantPrice() {
      if (!this.form) return 0;
      const variantInput = this.form.querySelector('[name="id"]');
      return Number(this.variantPrices[variantInput ? variantInput.value : ''] || 0);
    }

    getSelectedItems() {
      return this.inputs
        .filter((input) => input.checked && !input.disabled)
        .map((input) => ({
          id: Number(input.value),
          price: Number(input.dataset.price || 0),
          label: input.dataset.label || '',
          sku: input.dataset.sku || ''
        }));
    }

    onInputChange(changedInput) {
      const exclusiveGroup = changedInput.dataset.exclusiveGroup;
      if (changedInput.checked && exclusiveGroup) {
        this.inputs.forEach((input) => {
          if (input !== changedInput && input.dataset.exclusiveGroup === exclusiveGroup) {
            input.checked = false;
          }
        });
      }
      this.updateSummary();
    }

    clearError() {
      if (!this.error) return;
      this.error.hidden = true;
      this.error.textContent = '';
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    reset() {
      this.inputs.forEach((input) => { input.checked = false; });
      this.clearError();
      this.updateSummary();
    }

    updateSummary() {
      const selected = this.getSelectedItems();
      const cents = selected.reduce((sum, item) => sum + item.price, 0);
      if (this.summary) this.summary.hidden = selected.length === 0;
      if (this.total) this.total.textContent = `+ ${this.formatMoney(cents)}`;
      this.updateDisplayedPrice(cents);
      this.clearError();
      this.dispatchEvent(new CustomEvent('sd:extras-updated', { bubbles: true }));
    }

    updateDisplayedPrice(extrasCents = null) {
      const selectedCents = extrasCents === null
        ? this.getSelectedItems().reduce((sum, item) => sum + item.price, 0)
        : extrasCents;
      const baseCents = this.getCurrentVariantPrice();
      const priceWrapper = document.getElementById(`price-${this.dataset.sectionId}`);
      if (!priceWrapper || !baseCents) return;

      priceWrapper.querySelectorAll(
        '.price__regular .price-item--regular, .price__sale .price-item--sale'
      ).forEach((price) => {
        price.textContent = this.formatMoney(baseCents + selectedCents);
      });
      priceWrapper.toggleAttribute('data-sofa-extras-selected', selectedCents > 0);
    }

    async onSubmit(event) {
      const selectedExtras = this.getSelectedItems();
      if (!selectedExtras.length || !this.form) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.clearError();

      const productForm = this.form.closest('product-form');
      const submitButton = this.form.querySelector('[type="submit"]');
      const spinner = submitButton ? submitButton.querySelector('.loading-overlay__spinner') : null;
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      if (submitButton) {
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.classList.add('loading');
      }
      if (spinner) spinner.classList.remove('hidden');
      if (cart && typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);

      const formData = new FormData(this.form);
      const quantity = Math.max(1, Number(formData.get('quantity') || 1));
      const groupId = `sofa-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const properties = {};

      formData.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && value !== '') properties[match[1]] = value;
      });
      if (formData.has('selling_plan')) properties._Anzahlung = 'true';
      properties._sofa_extra_group = groupId;

      const mainItem = {
        id: Number(formData.get('id')),
        quantity,
        properties
      };
      if (formData.get('selling_plan')) mainItem.selling_plan = Number(formData.get('selling_plan'));

      // Shopify prepends items from a multi-add request to the cart. Send the
      // desired drawer order in reverse so the sofa is rendered first and its
      // extras follow underneath in the same order as on the product page.
      const currentVariantTitle = this.getCurrentVariantTitle();
      const extraItems = selectedExtras.map((extra) => {
        const extraProperties = {
          _sofa_extra_group: groupId,
          _sofa_extra_item: 'true',
          'Extra für': this.dataset.productTitle
        };

        if (
          currentVariantTitle &&
          (extra.sku === 'EXTRA-COUCHTISCH' || extra.sku === 'EXTRA-COUCHTISCH-LED')
        ) {
          extraProperties['Anfertigung wie Sofa'] = currentVariantTitle;
        }

        return {
          id: extra.id,
          quantity,
          properties: extraProperties
        };
      }).reverse();
      const items = extraItems.concat(mainItem);

      try {
        const payload = { items };
        if (cart && typeof cart.getSectionsToRender === 'function') {
          payload.sections = cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }

        const response = await fetch(`${window.routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(payload)
        });
        const state = await response.json();
        if (!response.ok || state.status) {
          throw new Error(state.description || state.message || 'Die Extras konnten nicht hinzugefügt werden.');
        }

        if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sofa-extras' });
        }
        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(state);
        } else {
          window.location = window.routes.cart_url;
        }
        this.reset();
      } catch (error) {
        this.showError(error.message || 'Die Extras konnten nicht hinzugefügt werden.');
        if (productForm && typeof productForm.handleErrorMessage === 'function') {
          productForm.handleErrorMessage(error.message);
        }
      } finally {
        if (submitButton) {
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('aria-disabled');
        }
        if (spinner) spinner.classList.add('hidden');
      }
    }

    formatMoney(cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
          style: 'currency',
          currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR'
        }).format(cents / 100);
      } catch (error) {
        return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
      }
    }
  });
}
